import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Truck, Clock, Award, Leaf } from 'lucide-react';
import axios from 'axios';
import { DishCard } from '../components/DishCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [featuredDishes, setFeaturedDishes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedDishes = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/dishes?featured=true`);
        setFeaturedDishes(response.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching dishes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedDishes();
  }, []);

  const features = [
    {
      icon: <Truck size={28} />,
      title: 'Livraison à Genève',
      description: 'Livraison soignée dans tout le canton, créneaux au choix.',
    },
    {
      icon: <Clock size={28} />,
      title: 'Commande agendée',
      description: 'Planifiez vos repas jusqu\'à 7 jours à l\'avance.',
    },
    {
      icon: <Award size={28} />,
      title: 'Qualité Premium',
      description: 'Ingrédients frais, recettes authentiques du monde.',
    },
    {
      icon: <Leaf size={28} />,
      title: 'Options Végétariennes',
      description: 'Une carte variée pour tous les régimes alimentaires.',
    },
  ];

  return (
    <main className="min-h-screen bg-transparent">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="container-padding max-w-7xl mx-auto pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="col-span-1 lg:col-span-5 space-y-8"
            >
              <div className="space-y-4">
                <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
                  Livraison à Genève
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-tight">
                  Racines et Épices{' '}
                  <span className="text-terracotta">du monde</span>
                </h1>
              </div>
              
              <p className="text-navy/70 text-lg leading-relaxed max-w-md">
                Des plats cuisinés avec passion, des recettes authentiques et équilibrées, préparés chaque jour avec les meilleurs ingrédients.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/menu"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-menu"
                >
                  Découvrir la carte
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/about"
                  className="btn-secondary inline-flex items-center justify-center"
                  data-testid="hero-cta-about"
                >
                  Notre histoire
                </Link>
              </div>
            </motion.div>
            
            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="col-span-1 lg:col-span-7 relative"
            >
              <div className="relative aspect-square lg:aspect-[4/3] overflow-hidden hero-blob">
                <img
                  src="https://images.unsplash.com/photo-1692197275441-40c874f40385?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="Plat gastronomique"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Floating Card - Logo */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute -bottom-6 -left-6 lg:bottom-8 lg:left-0 bg-white p-3 rounded-2xl shadow-float"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_quality-eats-2/artifacts/5z2har2n_image.png"
                  alt="Vraie cuisine maison - Real home cooking"
                  className="w-24 h-24 object-contain"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing bg-white/50">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center space-y-4"
              >
                <div className="w-16 h-16 mx-auto bg-terracotta/10 rounded-2xl flex items-center justify-center text-terracotta">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-xl font-semibold text-navy">
                  {feature.title}
                </h3>
                <p className="text-navy/60 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Dishes */}
      <section className="section-spacing">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
              Coups de coeur
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              Nos plats signatures
            </h2>
            <p className="text-navy/60 max-w-2xl mx-auto">
              Découvrez nos créations préférées, préparées avec amour et les meilleurs ingrédients du marché.
            </p>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white/50 rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : featuredDishes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredDishes.map((dish) => (
                <DishCard key={dish.dish_id} dish={dish} showAddToCart={false} />
              ))}
            </div>
          ) : (
            <p className="text-center text-navy/60">Les plats arrivent bientôt...</p>
          )}
          
          <div className="text-center mt-12">
            <Link
              to="/menu"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-full-menu"
            >
              Voir toute la carte
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[#3E2723] text-white">
        <div className="container-padding max-w-4xl mx-auto text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              Prêt à commander ?
            </h2>
            <p className="text-white/70 max-w-xl mx-auto">
              Réservez votre créneau de livraison et savourez des plats d'exception directement chez vous.
            </p>
          </motion.div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/menu"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="cta-order-now"
            >
              Commander maintenant
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
